import * as React from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from "lucide-react";
import { useRouter } from "@tanstack/react-router";

import { cn } from "~/utils";
import { buttonVariants, type Button } from "~/components/ui/button";

/*
  Non-stock fork (see docs/frontend-conventions.md): links navigate through
  the TanStack router with a raw `href` (so query strings keep their format)
  instead of a full page load, without resetting the scroll position, and
  Previous/Next/Ellipsis take a localized `label`. This coupling to the router
  cannot live in stock; re-apply on `shadcn diff`.
*/

function Pagination({ className, ...props }: React.ComponentProps<"nav">) {
  return (
    <nav
      // biome-ignore lint/a11y/noRedundantRoles: stock shadcn primitive
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn("mx-auto flex w-full justify-center", className)}
      {...props}
    />
  );
}

function PaginationContent({
  className,
  ...props
}: React.ComponentProps<"ul">) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn("flex flex-row items-center gap-1", className)}
      {...props}
    />
  );
}

function PaginationItem({ ...props }: React.ComponentProps<"li">) {
  return <li data-slot="pagination-item" {...props} />;
}

type PaginationHrefLinkProps = React.ComponentProps<"a"> & { href: string };

function PaginationHrefLink({
  href,
  onClick,
  children,
  ...props
}: PaginationHrefLinkProps) {
  const router = useRouter();

  return (
    <a
      href={href}
      onClick={(event) => {
        onClick?.(event);

        const isModifiedClick =
          event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

        if (event.defaultPrevented || event.button !== 0 || isModifiedClick) {
          return;
        }

        event.preventDefault();
        router.navigate({ href, resetScroll: false });
      }}
      {...props}
    >
      {children}
    </a>
  );
}

type PaginationLinkProps = {
  isActive?: boolean;
  disabled?: boolean;
} & Pick<React.ComponentProps<typeof Button>, "size"> &
  PaginationHrefLinkProps;

function PaginationLink({
  className,
  isActive,
  disabled,
  size = "icon",
  ...props
}: PaginationLinkProps) {
  return (
    <PaginationHrefLink
      aria-current={isActive ? "page" : undefined}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        buttonVariants({
          variant: isActive ? "outline" : "ghost",
          size,
        }),
        disabled && "pointer-events-none opacity-50",
        className,
      )}
      {...props}
    />
  );
}

function PaginationPrevious({
  className,
  label,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { label: string }) {
  return (
    <PaginationLink
      aria-label={label}
      size="default"
      className={cn("gap-1 px-2.5 sm:pl-2.5", className)}
      {...props}
    >
      <ChevronLeftIcon />
      <span className="hidden sm:block">{label}</span>
    </PaginationLink>
  );
}

function PaginationNext({
  className,
  label,
  ...props
}: React.ComponentProps<typeof PaginationLink> & { label: string }) {
  return (
    <PaginationLink
      aria-label={label}
      size="default"
      className={cn("gap-1 px-2.5 sm:pr-2.5", className)}
      {...props}
    >
      <span className="hidden sm:block">{label}</span>
      <ChevronRightIcon />
    </PaginationLink>
  );
}

function PaginationEllipsis({
  className,
  label,
  ...props
}: React.ComponentProps<"span"> & { label?: string }) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn("flex size-9 items-center justify-center", className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">{label ?? "More pages"}</span>
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationHrefLink,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
